import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { MatTabGroup } from '@angular/material/tabs';

export type MultiTabStatus = 'success' | 'error';

export interface MultiTabItem {
  label: string;
  imageSrc?: string;
  placeholder?: string;
  content?: SafeHtml | string;
  status?: MultiTabStatus;
}

@Component({
  selector: 'app-multi-tab',
  templateUrl: './multi-tab.component.html',
  styleUrls: ['./multi-tab.component.scss']
})
export class MultiTabComponent {
  @ViewChild(MatTabGroup) tabGroup?: MatTabGroup;
  @ViewChild('userInputTextarea') textareaRef?: ElementRef<HTMLTextAreaElement>;

  @Input() isLoading = false; 
  @Input() errorMessage: string | null = null;

  @Input() direction: 'rtl' | 'ltr' = 'rtl';
  @Input() selectedIndex = 0;

  @Input() tabs: MultiTabItem[] = [];

  @Input() tabOneLabel = 'Mistral';
  @Input() tabTwoLabel = 'Meta';
  @Input() tabThreeLabel = 'Claude';
  @Input() tabFourLabel = 'Cohere';
  @Input() tabFiveLabel = 'Summary';

  @Input() tabOneImageSrc?: string;
  @Input() tabTwoImageSrc?: string;
  @Input() tabThreeImageSrc?: string;
  @Input() tabFourImageSrc?: string;
  @Input() tabFiveImageSrc?: string;

  @Input() tabOnePlaceholder = 'Waiting for response...';
  @Input() tabTwoPlaceholder = 'Waiting for response...';
  @Input() tabThreePlaceholder = 'Waiting for response...';
  @Input() tabFourPlaceholder = 'Waiting for response...';
  @Input() tabFivePlaceholder = 'Waiting for response...';

  @Input() tabOneContent?: SafeHtml | string;
  @Input() tabTwoContent?: SafeHtml | string;
  @Input() tabThreeContent?: SafeHtml | string;
  @Input() tabFourContent?: SafeHtml | string;
  @Input() tabFiveContent?: SafeHtml | string;

  @Input() inputText = '';
  @Input() inputPlaceholder = 'Paste your prompt here...';
  @Input() inputDisabled = false;
  @Input() sendDisabled = false;
  @Input() showInput = true;
  @Input() showAudioButton = false;
  @Input() audioActive = false;

  @Input() showRepeatPromptButton = false;
  @Input() repeatPromptDisabled = false;
  @Input() repeatPromptHint = 'Press ↑ to repeat';

  @Input() showSelectorButton = false;
  @Input() selectorIconSrc?: string;
  @Input() selectorLabel = 'Select AI';

  @Input() showToolsMenuButton = false;
  @Input() toolsMenuOpen = false;

  @Input() showExportButtons = false;
  @Input() exportTabIndex = 4;

  @Output() exportPdf = new EventEmitter<void>();
  @Output() exportDocx = new EventEmitter<void>();
  @Output() inputTextChange = new EventEmitter<string>();
  @Output() submit = new EventEmitter<void>();
  @Output() submitPrompt = new EventEmitter<void>();
  @Output() audioClick = new EventEmitter<void>();
  @Output() audioToggle = new EventEmitter<void>();
  @Output() repeatPrompt = new EventEmitter<void>();
  @Output() openSelector = new EventEmitter<void>();
  @Output() toolsMenuToggle = new EventEmitter<void>();
  @Output() enterPressed = new EventEmitter<KeyboardEvent>();
  @Output() shiftEnterPressed = new EventEmitter<KeyboardEvent>();

  selectTab(index: number): void {
    if (this.tabGroup) {
      this.tabGroup.selectedIndex = index;
    }
  }

  onInputChange(value: string): void {
    this.inputText = value;
    this.inputTextChange.emit(value);
  }

  onEnter(event: KeyboardEvent): void {
    if (event.shiftKey) {
      this.shiftEnterPressed.emit(event);
      return;
    }
    this.enterPressed.emit(event);
    event.preventDefault();
    if (!this.sendDisabled && !this.inputDisabled) {
      this.submit.emit();
      this.submitPrompt.emit();
    }
  }

  onSubmit(): void {
    if (this.sendDisabled || this.inputDisabled) {
      return;
    }
    this.submit.emit();
    this.submitPrompt.emit();
  }

  onAudioToggle(): void {
    this.audioClick.emit();
    this.audioToggle.emit();
  }

  onSendOrAudio(): void {
    if (this.sendDisabled || this.inputDisabled) {
      return;
    }
    if (!this.inputText?.trim().length && this.showAudioButton) {
      this.onAudioToggle();
      return;
    }
    this.onSubmit();
  }

  onRepeatPrompt(): void {
    this.repeatPrompt.emit();
  }

  onOpenSelector(): void {
    this.openSelector.emit();
  }

  onToolsMenuToggle(): void {
    this.toolsMenuToggle.emit();
  }

  autoResizeTextarea(): void {
    const el = this.textareaRef?.nativeElement;
    if (!el) return;

    const maxHeight = 160;

    if (!el.value || el.value.trim().length === 0) {
      el.style.height = '';
      el.style.overflowY = 'hidden';
      return;
    }

    el.style.height = '';
    const scrollHeight = el.scrollHeight;
    const nextHeight = Math.min(scrollHeight, maxHeight);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  tabColor(index: number): string {
    return index % 2 === 0 ? '#800042' : '#008080';
  }
}
